import { useEffect, useRef } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { clsx } from 'clsx'

/**
 * PWA update prompt — shows banner when new version is available.
 * Uses registerType: 'prompt' strategy from vite-plugin-pwa.
 */
export default function ReloadPrompt() {
  const intervalRef = useRef<ReturnType<typeof setInterval>>(undefined)

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      if (r) {
        // Clear any previous interval on re-registration
        if (intervalRef.current) clearInterval(intervalRef.current)
        // Check for updates every 30 minutes
        intervalRef.current = setInterval(() => r.update(), 30 * 60 * 1000)
      }
    },
  })

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  if (!needRefresh) return null

  return (
    <div
      role="alert"
      className={clsx(
        'fixed bottom-4 left-1/2 -translate-x-1/2 z-[200]',
        'flex items-center gap-3 px-4 py-3 rounded-xl',
        'bg-white shadow-lg border border-neutral-200',
        'animate-in fade-in slide-in-from-bottom-4 duration-300',
        'max-w-sm w-full mx-4'
      )}
    >
      <div className="flex-1 text-sm text-neutral-700">
        Доступно обновление
      </div>
      <button
        onClick={() => {
          try {
            updateServiceWorker(true)
          } catch {
            // Fallback: force reload if SW update fails (Vite 8 compat)
            window.location.reload()
          }
          // Safety net: if nothing happens after 3s, force reload
          setTimeout(() => window.location.reload(), 3000)
        }}
        className={clsx(
          'px-3 py-1.5 rounded-lg text-sm font-medium',
          'bg-sky-500 text-white hover:bg-sky-600',
          'transition-colors'
        )}
      >
        Обновить
      </button>
      <button
        onClick={() => setNeedRefresh(false)}
        className="text-neutral-400 hover:text-neutral-600 transition-colors"
        aria-label="Закрыть"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}
